import { z } from 'zod';

export const DEFAULT_TIMEZONE = 'Asia/Tashkent';

const CURATED_TIMEZONES: readonly string[] = [
  'Asia/Tashkent',
  'Asia/Seoul',
  'Asia/Tokyo',
  'UTC',
  'Europe/Moscow',
] as const;

export const getTimezoneOptions = (): string[] => {
  const supported =
    typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [];
  const merged = [DEFAULT_TIMEZONE, ...supported];
  const unique = Array.from(new Set(merged.length > 1 ? merged : CURATED_TIMEZONES));
  // Keep Asia/Tashkent first, the rest sorted alphabetically.
  const rest = unique.filter((tz) => tz !== DEFAULT_TIMEZONE).sort((a, b) => a.localeCompare(b));
  return [DEFAULT_TIMEZONE, ...rest];
};

export const limitConfigSchema = z.object({
  // An emptied field must fail validation on submit (not silently become 0).
  amount: z.preprocess(
    (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
    z.coerce
      .number({ invalid_type_error: 'limitConfig.form.errors.amountInvalid' })
      .min(0, 'limitConfig.form.errors.amountMin'),
  ),
  period_unit: z.enum(['DAY', 'MONTH']),
  period_value: z.coerce
    .number({ invalid_type_error: 'limitConfig.form.errors.periodValueInvalid' })
    .int('limitConfig.form.errors.periodValueInvalid')
    .min(1, 'limitConfig.form.errors.periodValueMin'),
  start_day: z.coerce
    .number({ invalid_type_error: 'limitConfig.form.errors.startDayInvalid' })
    .int('limitConfig.form.errors.startDayInvalid')
    .min(1, 'limitConfig.form.errors.startDayRange')
    .max(31, 'limitConfig.form.errors.startDayRange'),
  start_date: z.string().trim(),
  timezone: z.string().trim().min(1, 'limitConfig.form.errors.timezoneRequired'),
  reset_usage: z.boolean(),
});

export type LimitConfigFormValues = z.infer<typeof limitConfigSchema>;
